/**
 * ToonRunner — LC 학습 흐름에서 깨봉툰(intro/outro) 재생을 담당
 *
 * - shared-learning 의 useToonPlayer 훅으로 페이지/오디오 상태 관리
 * - 디자인은 apps/lc/DESIGN.md 의 LC 토큰을 따름
 * - 모션은 legacy student-web 의 ToonIntro/ToonOutro 동작을 그대로 재현
 *   · 페이지 전환: 좌/우 슬라이드 + 페이드
 *   · 인트로: v3 평면 카드/슬라이더, 마지막 페이지 완료 버튼
 *   · 아웃트로 "완료" 버튼: 마지막 페이지에서 아래에서 위로 슬라이드 (delay 1s)
 *   · 오답 버튼: 흔들림 (y shake)
 *   · 정답 버튼: 중앙으로 이동, 상대 버튼은 아래로 슬라이드
 *   · 정/오답 Lottie 피드백 + 효과음
 *   · 다음 버튼: Lottie 반응 애니메이션
 * - 마지막 카드의 완료 버튼 클릭 시 completeSession 호출
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Lottie from 'lottie-react'
import { ChevronLeft, ChevronRight, Volume2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useToonPlayer,
  type ToonCard,
  type ToonQuizCard,
} from '@quebon/shared-learning'
import {
  completeSession,
  type LcToonData,
} from '@/api/lcLearning'
import { Button } from './ui/Button'
import { Button3D } from './ui/Button3D'
import { Card } from './ui/Card'
import { Mascot } from './ui/Mascot'
import { cn } from '@/lib/utils'
import rightAnimation from '@/assets/toon/lottie/toon_outro_right.json'
import wrongAnimation from '@/assets/toon/lottie/toon_outro_wrong.json'
import rightSoundSrc from '@/assets/toon/sounds/toon_outro_right_sound.mp3'
import wrongSoundSrc from '@/assets/toon/sounds/toon_outro_wrong_sound.mp3'
import type { RuntimeMode } from './ComponentRunner'

interface Props {
  componentProgressId: number | null
  enrollmentId: number
  componentType: string
  toonType: 'intro' | 'outro'
  toonData: LcToonData | null
  runtimeMode?: RuntimeMode
  onComplete: () => void
}

type AnswerKey = 'answer_0' | 'answer_1'

const slideVariants = {
  enter: (dir: 'left' | 'right') => ({
    x: dir === 'right' ? 300 : -300,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (dir: 'left' | 'right') => ({
    x: dir === 'right' ? -300 : 300,
    opacity: 0,
  }),
}

export default function ToonRunner({
  componentProgressId,
  enrollmentId,
  componentType,
  toonType,
  toonData,
  runtimeMode = 'live',
  onComplete,
}: Props) {
  const { t } = useTranslation('learner')
  const isLive = runtimeMode === 'live'
  const [completing, setCompleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleComplete = useCallback(async () => {
    if (completing) return
    setCompleting(true)
    setError(null)
    try {
      if (isLive && componentProgressId != null) {
        await completeSession(componentProgressId, {
          enrollmentId,
          componentType,
        })
      }
      onComplete()
    } catch (e) {
      console.error('Toon completeSession failed', e)
      setError(t('learn.toon.errors.complete'))
      setCompleting(false)
    }
  }, [completing, isLive, componentProgressId, enrollmentId, componentType, onComplete, t])

  const introCards: ToonCard[] = toonData?.qnIntro ?? []
  const outroCards: ToonQuizCard[] = toonData?.qnOutro ?? []
  const hasIntro = toonType === 'intro' && introCards.length > 0
  const hasOutro = toonType === 'outro' && outroCards.length > 0

  if (hasIntro) {
    return (
      <ToonIntroView
        cards={introCards}
        onComplete={handleComplete}
        completing={completing}
        error={error}
        labels={{
          audio: t('learn.toon.audio'),
          previous: t('learn.toon.previous'),
          next: t('learn.toon.next'),
          complete: t('learn.toon.complete'),
          completing: t('learn.toon.completing'),
        }}
      />
    )
  }

  if (hasOutro) {
    return (
      <ToonOutroView
        cards={outroCards}
        onComplete={handleComplete}
        completing={completing}
        error={error}
        labels={{
          audio: t('learn.toon.audio'),
          previous: t('learn.toon.previous'),
          next: t('learn.toon.next'),
          complete: t('learn.toon.complete'),
          completing: t('learn.toon.completing'),
        }}
      />
    )
  }

  return <ToonPlaceholder onComplete={handleComplete} completing={completing} error={error} />
}

interface ToonLabels {
  audio: string
  previous: string
  next: string
  complete: string
  completing: string
}

/* ------------------------------------------------------------------ */
/* Shared audio/error helpers                                         */
/* ------------------------------------------------------------------ */

function useToonAudio(onNarrationFinished: (page: number) => void) {
  const narrationAudioRef = useRef<HTMLAudioElement | null>(null)
  const effectAudioRef = useRef<HTMLAudioElement | null>(null)
  const narrationPageRef = useRef<number | null>(null)
  const onNarrationFinishedRef = useRef(onNarrationFinished)
  onNarrationFinishedRef.current = onNarrationFinished

  useEffect(() => {
    const narrationAudio = new Audio()
    const effectAudio = new Audio()
    const finishNarration = () => {
      const page = narrationPageRef.current
      if (page != null) onNarrationFinishedRef.current(page)
    }

    narrationAudio.onended = finishNarration
    // 손상되거나 불러올 수 없는 음성 때문에 학습 흐름이 영구 차단되지 않게 한다.
    narrationAudio.onerror = finishNarration
    narrationAudioRef.current = narrationAudio
    effectAudioRef.current = effectAudio

    return () => {
      narrationAudio.pause()
      effectAudio.pause()
      narrationAudio.onended = null
      narrationAudio.onerror = null
      narrationAudioRef.current = null
      effectAudioRef.current = null
    }
  }, [])

  const playNarration = useCallback((src: string, page: number) => {
    const audio = narrationAudioRef.current
    if (!audio) return
    narrationPageRef.current = page
    audio.src = src
    audio.currentTime = 0
    audio.play().catch(() => {})
  }, [])

  const stopNarration = useCallback(() => {
    narrationAudioRef.current?.pause()
    narrationPageRef.current = null
  }, [])

  const playSoundEffect = useCallback((src: string) => {
    const audio = effectAudioRef.current
    if (!audio) return
    audio.src = src
    audio.currentTime = 0
    audio.play().catch(() => {})
  }, [])

  return { playNarration, stopNarration, playSoundEffect }
}


function ErrorCard({ message }: { message: string }) {
  return (
    <Card size="md" className="w-full max-w-lg mx-auto flex items-center gap-3 mt-4">
      <div
        aria-label="error"
        className="w-8 h-8 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[18px] font-bold shrink-0"
      >
        ✕
      </div>
      <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-red-dark">{message}</p>
    </Card>
  )
}

/* ------------------------------------------------------------------ */
/* Intro view                                                          */
/* ------------------------------------------------------------------ */

function ToonIntroView({
  cards,
  onComplete,
  completing,
  error,
  labels,
}: {
  cards: ToonCard[]
  onComplete: () => void
  completing: boolean
  error: string | null
  labels: ToonLabels
}) {
  const { currentPage, direction, isFirst, isLast, goNext, goPrev } = useToonPlayer({
    totalPages: cards.length,
  })
  const [narratedPages, setNarratedPages] = useState<Set<number>>(() => new Set())
  const markNarrationFinished = useCallback((page: number) => {
    setNarratedPages((previous) => {
      if (previous.has(page)) return previous
      const next = new Set(previous)
      next.add(page)
      return next
    })
  }, [])
  const { playNarration, stopNarration } = useToonAudio(markNarrationFinished)

  const card = cards[currentPage]
  const isNarrationComplete = !card?.audioUrl || narratedPages.has(currentPage)
  const playCurrentNarration = useCallback(
    (src?: string) => {
      if (src) playNarration(src, currentPage)
    },
    [currentPage, playNarration],
  )

  useEffect(() => {
    if (card?.audioUrl) playNarration(card.audioUrl, currentPage)
    else stopNarration()
    return stopNarration
  }, [currentPage, card?.audioUrl, playNarration, stopNarration])

  return (
    <div className="flex h-full min-h-[520px] w-full flex-col">
      <div className="relative flex min-h-0 flex-1 flex-col items-center gap-6 overflow-hidden rounded-[20px] bg-gradient-to-r from-white to-[#f9fafb] px-5 pb-6 pt-[26px] shadow-[0_4px_8px_0_rgb(0_0_0/0.05)]">
        <div className="flex h-16 w-full max-w-[840px] shrink-0 items-center justify-center gap-4 rounded-2xl bg-[#f0f0f0] py-3 pl-4 pr-10">
          {card?.audioUrl ? (
            <button
              type="button"
              onClick={() => playCurrentNarration(card.audioUrl)}
              aria-label={labels.audio}
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-lc-warning-600 text-white transition-colors hover:bg-lc-warning-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              <Volume2 className="size-6 [&_polygon]:fill-current" strokeWidth={2} aria-hidden />
            </button>
          ) : null}
          <p className="min-w-0 flex-1 text-center text-[24px] font-medium leading-[1.5] tracking-[-0.6px] text-lc-gray-700">
            {card?.text || '??'}
          </p>
        </div>

        <div className="relative h-[320px] w-full max-w-[840px] shrink-0">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="absolute inset-0 flex items-center justify-center px-12"
            >
              {card?.imageUrl ? (
                <img
                  src={card.imageUrl}
                  alt=""
                  className="h-[320px] w-full max-w-[640px] object-cover"
                />
              ) : null}
            </motion.div>
          </AnimatePresence>

          {isFirst ? null : (
            <button
              type="button"
              onClick={goPrev}
              aria-label={labels.previous}
              className="absolute left-0 top-1/2 inline-flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-lc-v3-gray-300 bg-lc-v3-gray-50 text-[#aaaaaa] transition-colors hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              <ChevronLeft className="size-6" aria-hidden />
            </button>
          )}
          {isLast ? null : (
            <button
              type="button"
              onClick={goNext}
              disabled={!isNarrationComplete}
              aria-label={labels.next}
              className="absolute right-0 top-1/2 inline-flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-lc-key-500 text-white transition-[background-color,opacity] hover:bg-lc-key-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-lc-key-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              <ChevronRight className="size-6" aria-hidden />
            </button>
          )}
        </div>

        <div className="flex w-full shrink-0 items-center justify-center gap-[14px] p-2">
          {Array.from({ length: cards.length }).map((_, index) => (
            <span
              key={index}
              className={cn(
                'size-2.5 rounded-full',
                index === currentPage ? 'bg-lc-key-900' : 'bg-[#aaaaaa]',
              )}
              aria-hidden
            />
          ))}
        </div>

        {isLast ? (
          <div className="pointer-events-none absolute inset-x-5 top-[448px] flex justify-center">
            <motion.div
              initial={{ y: '200%' }}
              animate={{ y: '0%' }}
              transition={{ delay: 1, duration: 0.4, ease: 'easeOut' }}
              className="pointer-events-auto flex w-full max-w-[840px] justify-end"
            >
              <Button
                variant="primary"
                size="L"
                className="min-w-[104px] disabled:text-white!"
                onClick={onComplete}
                disabled={completing || !isNarrationComplete}
              >
                {completing ? labels.completing : labels.complete}
              </Button>
            </motion.div>
          </div>
        ) : null}
      </div>

      {error && <ErrorCard message={error} />}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Outro view (2-choice quiz)                                          */
/* ------------------------------------------------------------------ */

function ToonOutroView({
  cards,
  onComplete,
  completing,
  error,
  labels,
}: {
  cards: ToonQuizCard[]
  onComplete: () => void
  completing: boolean
  error: string | null
  labels: ToonLabels
}) {
  const {
    currentPage,
    direction,
    isFirst,
    isLast,
    goNext,
    goPrev,
  } = useToonPlayer({
    totalPages: cards.length,
  })

  const [answeredPages, setAnsweredPages] = useState<Set<number>>(new Set())
  const [playingAnimation, setPlayingAnimation] = useState<AnswerKey | null>(null)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  const [narratedPages, setNarratedPages] = useState<Set<number>>(() => new Set())
  const markNarrationFinished = useCallback((page: number) => {
    setNarratedPages((previous) => {
      if (previous.has(page)) return previous
      const next = new Set(previous)
      next.add(page)
      return next
    })
  }, [])
  const { playNarration, stopNarration, playSoundEffect } = useToonAudio(
    markNarrationFinished,
  )

  const card = cards[currentPage]
  const isPageAnswered = answeredPages.has(currentPage)
  const isNarrationComplete = !card?.audioUrl || narratedPages.has(currentPage)
  const hasMultiplePages = cards.length > 1
  const playCurrentNarration = useCallback(
    (src?: string) => {
      if (src) playNarration(src, currentPage)
    },
    [currentPage, playNarration],
  )

  useEffect(() => {
    setPlayingAnimation(null)
    setIsCorrect(null)
    if (card?.audioUrl) playNarration(card.audioUrl, currentPage)
    else stopNarration()
    return stopNarration
  }, [currentPage, card?.audioUrl, playNarration, stopNarration])

  const checkAnswer = useCallback(
    (answer: AnswerKey) => {
      if (
        !card ||
        isPageAnswered ||
        playingAnimation !== null
      ) {
        return
      }
      const correct = answer === card.correctAnswer
      setIsCorrect(correct)
      setPlayingAnimation(answer)
      if (correct) {
        setAnsweredPages((prev) => {
          const next = new Set(prev)
          next.add(currentPage)
          return next
        })
        window.setTimeout(() => playSoundEffect(rightSoundSrc), 800)
      } else {
        playSoundEffect(wrongSoundSrc)
      }
    },
    [card, currentPage, isPageAnswered, playSoundEffect, playingAnimation],
  )

  const handleFeedbackAnimationComplete = useCallback(() => {
    setPlayingAnimation(null)
    setIsCorrect(null)
  }, [])

  const getButtonAnimation = (answerKey: AnswerKey, toon: ToonQuizCard) => {
    const isThisAnswer = playingAnimation === answerKey
    const pageAnswered = answeredPages.has(currentPage)
    const isCorrectAnswer = toon.correctAnswer === answerKey

    if (isThisAnswer && isCorrect === false) {
      return { y: [-40, 40, -20, 20, 0] }
    }
    if ((isThisAnswer && isCorrect === true) || (pageAnswered && isCorrectAnswer)) {
      return { x: answerKey === 'answer_0' ? 172 : -172 }
    }
    if (pageAnswered && !isCorrectAnswer) {
      return { y: '500%', opacity: 0 }
    }
    return { x: 0, y: 0, opacity: 1 }
  }

  const getButtonClasses = (answerKey: AnswerKey, toon: ToonQuizCard) => {
    const base =
      'relative flex h-20 w-[328px] shrink-0 items-center justify-center overflow-hidden rounded-xl px-5 py-3 text-center text-[20px] font-semibold leading-[1.5] tracking-[-0.6px] text-white transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500'

    const isThisAnswer = playingAnimation === answerKey
    const isCorrectAnswer = toon.correctAnswer === answerKey
    const pageAnswered = answeredPages.has(currentPage)

    // 정답 선택: 가운데로 이동한 success 버튼.
    if (pageAnswered && isCorrectAnswer) {
      return `${base} bg-lc-success-500`
    }
    if (isThisAnswer && isCorrect === true) {
      return `${base} bg-lc-success-500`
    }
    // 오답 Lottie가 재생되는 동안만 Gray 400으로 피드백한다.
    if (isThisAnswer && isCorrect === false) {
      return `${base} bg-lc-v3-gray-400`
    }
    return `${base} bg-lc-key-500 hover:bg-lc-key-600`
  }

  return (
    <div className="flex h-full min-h-[520px] w-full flex-col">
      <div className="relative flex min-h-0 flex-1 flex-col items-center gap-6 overflow-hidden rounded-[20px] bg-gradient-to-r from-white to-[#f9fafb] px-5 pb-6 pt-[26px] shadow-[0_4px_8px_0_rgb(0_0_0/0.05)]">
        <div className="flex h-16 w-full max-w-[840px] shrink-0 items-center justify-center gap-4 rounded-2xl bg-[#f0f0f0] py-3 pl-4 pr-10">
          {card?.audioUrl ? (
            <button
              type="button"
              onClick={() => playCurrentNarration(card.audioUrl)}
              aria-label={labels.audio}
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-lc-warning-600 text-white transition-colors hover:bg-lc-warning-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              <Volume2 className="size-6 [&_polygon]:fill-current" strokeWidth={2} aria-hidden />
            </button>
          ) : null}
          <p className="min-w-0 flex-1 text-center text-[24px] font-medium leading-[1.5] tracking-[-0.6px] text-lc-gray-700">
            {card?.text || '??'}
          </p>
        </div>

        <div className="relative flex w-full max-w-[840px] shrink-0 flex-col items-center justify-center gap-2 px-[52px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="flex w-full flex-col items-center gap-2"
            >
              <div className="relative flex h-[320px] w-full items-center justify-center">
                {card?.imageUrl ? (
                  <img
                    src={card.imageUrl}
                    alt=""
                    className="max-h-[320px] max-w-full object-contain"
                  />
                ) : null}
              </div>

              {card?.answer0 && card?.answer1 ? (
                <div className="flex w-full justify-center gap-4">
                  {(['answer_0', 'answer_1'] as const).map((answerKey) => {
                    const text = answerKey === 'answer_0' ? card.answer0 : card.answer1
                    const isThisAnswer = playingAnimation === answerKey
                    const isCorrectAnswer = card.correctAnswer === answerKey
                    const isHiddenAfterCorrect =
                      isPageAnswered && !isCorrectAnswer
                    let answerState = 'idle'
                    if (isHiddenAfterCorrect) answerState = 'leaving'
                    else if (isPageAnswered && isCorrectAnswer) answerState = 'correct'
                    else if (isThisAnswer && isCorrect === false) answerState = 'wrong-feedback'
                    return (
                      <motion.button
                        key={answerKey}
                        animate={getButtonAnimation(answerKey, card)}
                        transition={{ duration: 0.5, ease: 'linear' }}
                        className={cn(
                          getButtonClasses(answerKey, card),
                          isHiddenAfterCorrect && 'pointer-events-none',
                        )}
                        onClick={() => checkAnswer(answerKey)}
                        disabled={isPageAnswered || playingAnimation !== null}
                        aria-hidden={isHiddenAfterCorrect}
                        tabIndex={isHiddenAfterCorrect ? -1 : undefined}
                        data-answer-state={answerState}
                      >
                        {text}
                        {playingAnimation === answerKey && (
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                            <Lottie
                              animationData={isCorrect ? rightAnimation : wrongAnimation}
                              loop={false}
                              autoplay
                              style={{ width: 120, height: 120 }}
                              onComplete={handleFeedbackAnimationComplete}
                            />
                          </div>
                        )}
                      </motion.button>
                    )
                  })}
                </div>
              ) : null}
            </motion.div>
          </AnimatePresence>

          {hasMultiplePages ? (
            <>
              {isFirst ? null : (
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label={labels.previous}
                  className="absolute left-0 top-40 inline-flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-lc-v3-gray-300 bg-lc-v3-gray-50 text-[#aaaaaa] transition-colors hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
                >
                  <ChevronLeft className="size-6" aria-hidden />
                </button>
              )}
              {isLast ? null : (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!isPageAnswered || !isNarrationComplete}
                  aria-label={labels.next}
                  className="absolute right-0 top-40 inline-flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-lc-key-500 text-white transition-[background-color,opacity] hover:bg-lc-key-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-lc-key-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
                >
                  <ChevronRight className="size-6" aria-hidden />
                </button>
              )}
            </>
          ) : null}
        </div>

        {hasMultiplePages ? (
          <div className="flex w-full shrink-0 items-center justify-center gap-[14px] p-2">
            {Array.from({ length: cards.length }).map((_, index) => (
              <span
                key={index}
                className={cn(
                  'size-2.5 rounded-full',
                  index === currentPage ? 'bg-lc-key-900' : 'bg-[#aaaaaa]',
                )}
                aria-hidden
              />
            ))}
          </div>
        ) : null}

        {isLast && isPageAnswered ? (
          <div className="pointer-events-none absolute inset-x-5 top-[474px] flex justify-center">
            <motion.div
              initial={{ y: '200%' }}
              animate={{ y: '0%' }}
              transition={{ delay: 0.8, duration: 0.4, ease: 'easeOut' }}
              className="pointer-events-auto flex w-full max-w-[840px] justify-end"
            >
              <Button
                variant="primary"
                size="L"
                className="min-w-[104px] disabled:text-white!"
                onClick={onComplete}
                disabled={completing || !isNarrationComplete}
              >
                {completing ? labels.completing : labels.complete}
              </Button>
            </motion.div>
          </div>
        ) : null}
      </div>

      {error && <ErrorCard message={error} />}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Placeholder                                                         */
/* ------------------------------------------------------------------ */

function ToonPlaceholder({
  onComplete,
  completing,
  error,
}: {
  onComplete: () => void
  completing: boolean
  error: string | null
}) {
  return (
    <div className="flex flex-col items-center gap-6 min-h-[40vh] max-w-lg mx-auto">
      <Card size="lg" className="w-full flex flex-col items-center gap-4 py-10 text-center">
        <Mascot name="queda" shape="roundbox" size={80} />
        <h2 className="text-[24px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-gray-900">
          깨봉툰은 준비 중이에요
        </h2>
        <p className="text-[18px] leading-[1.5] tracking-[-0.6px] font-normal text-lc-gray-600">
          나중에 다시 확인해주세요.
        </p>
      </Card>

      {error && <ErrorCard message={error} />}

      <Button3D variant="orange" size="L" onClick={onComplete} disabled={completing}>
        {completing ? '처리 중...' : '확인'}
      </Button3D>
    </div>
  )
}
